import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { formatNewsContext, getNewsByCategory, getTrendingTopics } from '@/api-frontend/formatNewsContext';

interface FormattedNewsContext {
  summary: string;
  topHeadlines: Array<{
    title: string;
    category: string;
    source: string;
    timeAgo: string;
    conversationStarter: string;
  }>;
  categories: string[];
  lastUpdated: string;
  totalItems: number;
}

interface NewsState {
  newsContext: FormattedNewsContext | null;
  categoryNews: Record<string, FormattedNewsContext>;
  trendingTopics: string[];
  loading: boolean;
  error: string | null;
  lastFetched: string | null;
}

const initialState: NewsState = {
  newsContext: null,
  categoryNews: {},
  trendingTopics: [],
  loading: false,
  error: null,
  lastFetched: null,
};

// Async thunks for news operations
export const fetchNewsContext = createAsyncThunk(
  'news/fetchNewsContext',
  async (options: { 
    category?: string; 
    location?: string; 
    maxItems?: number;
    includeCategories?: string[];
    excludeKeywords?: string[];
  } = {}) => {
    const result = await formatNewsContext(options);
    return result;
  }
);

export const fetchNewsByCategory = createAsyncThunk(
  'news/fetchNewsByCategory',
  async ({ category, maxItems = 5 }: { category: string; maxItems?: number }) => {
    const result = await getNewsByCategory(category, maxItems);
    return { category, data: result };
  }
);

export const fetchTrendingTopics = createAsyncThunk(
  'news/fetchTrendingTopics',
  async (location?: string) => {
    const result = await getTrendingTopics(location);
    return result;
  }
);

const newsSlice = createSlice({
  name: 'news',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearNewsContext: (state) => {
      state.newsContext = null;
      state.lastFetched = null;
    },
    clearCategoryNews: (state, action: PayloadAction<string>) => {
      delete state.categoryNews[action.payload];
    },
    clearAllNews: (state) => {
      state.newsContext = null;
      state.categoryNews = {};
      state.trendingTopics = [];
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchNewsContext
      .addCase(fetchNewsContext.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNewsContext.fulfilled, (state, action) => {
        state.loading = false;
        state.newsContext = action.payload;
        state.lastFetched = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchNewsContext.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch news context';
      })

      // fetchNewsByCategory
      .addCase(fetchNewsByCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNewsByCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categoryNews[action.payload.category] = action.payload.data;
        state.error = null;
      })
      .addCase(fetchNewsByCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch category news';
      })

      // fetchTrendingTopics
      .addCase(fetchTrendingTopics.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchTrendingTopics.fulfilled, (state, action) => {
        state.trendingTopics = action.payload;
        state.error = null;
      })
      .addCase(fetchTrendingTopics.rejected, (state, action) => {
        state.error = action.error.message ?? 'Failed to fetch trending topics';
      });
  },
});

export const { 
  clearError, 
  clearNewsContext, 
  clearCategoryNews, 
  clearAllNews 
} = newsSlice.actions;

export default newsSlice.reducer;